import { useEffect, useRef, useState } from "react";
import { getJson, ago, nf, ConfidenceBadge, TYPE_LABEL } from "../kb/shared";
import { StixExportLink } from "../kb/Stix";

// Drill-down panel: the records behind a chart value — the claims in a week, the
// victims in a country, the indicators of a family. Every entity in it links to
// its Knowledge Base profile.
//
// request = { title, subtitle, params: { dataset, filters, scope, range, match } }

const PAGE = 50;

function rowsUrl(params, offset) {
  const p = new URLSearchParams({ dataset: params.dataset, range: params.range || "all", offset: String(offset), limit: String(PAGE) });
  if (params.q) p.set("q", params.q);
  if (params.filters?.length) p.set("filters", JSON.stringify(params.filters.map(({ dim, values, exclude }) => ({ dim, values, exclude: !!exclude }))));
  if (params.scope) p.set("scope", JSON.stringify(params.scope));
  if (params.match) p.set("match", JSON.stringify(params.match));
  return `/api/dash/rows?${p}`;
}

// The same rows as a STIX 2.1 bundle (stix-export.js on the server).
function stixUrl(params, title) {
  const p = new URLSearchParams(rowsUrl(params, 0).split("?")[1]);
  p.delete("offset"); p.delete("limit");
  if (title) p.set("title", title);
  return `/api/kb/stix/dash?${p}`;
}

const kbHref = (link) => `#/kb/e/${encodeURIComponent(link)}`;
const fmtDay = (d) => {
  const t = new Date(String(d).length === 10 ? d + "T00:00:00Z" : d);
  return Number.isNaN(t.getTime()) ? "" : t.toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric", timeZone: "UTC" });
};

function EntityLink({ e, ui, onNavigate }) {
  const { B, fs } = ui;
  if (!e) return null;
  if (!e.link) return <span style={{ color: B.text, fontWeight: 700 }}>{e.label}</span>;
  return <a href={kbHref(e.link)} onClick={onNavigate} style={{ color: B.text, fontWeight: 700, textDecoration: "none", borderBottom: `1px dotted ${B.textDim}`, fontSize: fs(12) }}>{e.label}</a>;
}

function Row({ dataset, r, ui, onNavigate }) {
  const { B, fs, TypeIcon, getConf } = ui;
  const line = { display: "flex", gap: 8, alignItems: "baseline", flexWrap: "wrap", fontSize: fs(12) };
  const dim = { color: B.textDim, fontSize: fs(11) };
  if (dataset === "victims") {
    return (
      <>
        <div style={{ ...line, color: B.text }}>
          {r.vid
            ? <a href={`#/kb/victim/${encodeURIComponent(r.vid)}`} onClick={onNavigate} style={{ fontWeight: 600, flex: "1 1 200px", minWidth: 0, wordBreak: "break-word", color: B.text, textDecoration: "none", borderBottom: `1px dotted ${B.textDim}` }}>{r.title || "Unnamed victim"}</a>
            : <span style={{ fontWeight: 600, flex: "1 1 200px", minWidth: 0, wordBreak: "break-word" }}>{r.title || "Unnamed victim"}</span>}
          <span style={dim}>{fmtDay(r.date)}</span>
        </div>
        <div style={{ ...line, marginTop: 3 }}>
          <span style={dim}>claimed by</span> <EntityLink e={r.group} ui={ui} onNavigate={onNavigate} />
          {r.country && <span style={dim}>· {r.country.name}</span>}
          {r.sector && r.sector !== "Unknown" && <a href={`#/kb/sector/${encodeURIComponent(r.sector)}`} onClick={onNavigate} style={{ ...dim, textDecoration: "none", borderBottom: `1px dotted ${B.textDim}` }}>· {r.sector}</a>}
        </div>
      </>
    );
  }
  if (dataset === "indicators") {
    return (
      <>
        <div style={{ ...line, alignItems: "center" }}>
          <TypeIcon type={r.type} color={getConf(r.type).color} size={13} />
          <a href={kbHref(r.value)} onClick={onNavigate} style={{ fontFamily: "monospace", color: B.text, textDecoration: "none", borderBottom: `1px dotted ${B.textDim}`, wordBreak: "break-all", flex: "1 1 180px", minWidth: 0 }}>{r.value}</a>
          <ConfidenceBadge confidence={r.confidence} ui={ui} />
        </div>
        <div style={{ ...line, marginTop: 3 }}>
          {r.families.map(f => <EntityLink key={f.link} e={f} ui={ui} onNavigate={onNavigate} />)}
          <span style={dim}>{[TYPE_LABEL[r.type], ...r.threatTypes].join(" · ")} · {r.sources.join(", ")} · last seen {ago(r.lastSeen)}</span>
        </div>
      </>
    );
  }
  if (dataset === "vulnerabilities") {
    return (
      <>
        <div style={line}>
          <a href={kbHref(r.cve)} onClick={onNavigate} style={{ fontFamily: "monospace", fontWeight: 700, color: B.text, textDecoration: "none", borderBottom: `1px dotted ${B.textDim}` }}>{r.cve}</a>
          {r.ransomware && <span style={{ fontSize: fs(10), color: "#d03b3b", border: "1px solid #d03b3b55", borderRadius: 10, padding: "0 6px" }}>⚠ used by ransomware</span>}
          <span style={{ ...dim, marginLeft: "auto" }}>added {fmtDay(r.date)}</span>
        </div>
        <div style={{ fontSize: fs(12), color: B.textMuted, marginTop: 3 }}>{r.name}</div>
      </>
    );
  }
  if (dataset === "groups") {
    return (
      <div style={line}>
        <EntityLink e={r.group} ui={ui} onNavigate={onNavigate} />
        <span style={dim}>{r.source}{r.origin.length ? ` · attributed to ${r.origin.join(", ")}` : ""}{r.victims ? ` · ${nf(r.victims)} claimed victims` : ""}</span>
      </div>
    );
  }
  // ATT&CK usage rows: "<group or software> uses <technique or software>"
  return (
    <div style={{ ...line, alignItems: "center" }}>
      {r.user && <TypeIcon type={r.user.type} color={getConf(r.user.type).color} size={13} />}
      <EntityLink e={r.user} ui={ui} onNavigate={onNavigate} />
      <span style={dim}>uses</span>
      <EntityLink e={r.technique || r.software} ui={ui} onNavigate={onNavigate} />
    </div>
  );
}

function toCsv(dataset, rows) {
  const cell = (v) => { const s = String(v ?? ""); return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s; };
  const map = {
    victims: [["Victim", "Group", "Country", "Sector", "Claimed"], r => [r.title, r.group?.label, r.country?.name, r.sector, r.date]],
    indicators: [["Value", "Type", "Families", "Threat types", "Sources", "First seen", "Last seen", "Confidence"], r => [r.value, r.type, r.families.map(f => f.label).join("; "), r.threatTypes.join("; "), r.sources.join("; "), r.firstSeen, r.lastSeen, `${r.confidence.label} ${r.confidence.score}`]],
    vulnerabilities: [["CVE", "Vendor", "Product", "Name", "Added", "Ransomware use"], r => [r.cve, r.vendor, r.product, r.name, r.date, r.ransomware ? "Known" : ""]],
    groups: [["Group", "Source", "Origin", "Claimed victims"], r => [r.group.label, r.source, r.origin.join("; "), r.victims]],
  }[dataset] || [["Who", "Uses"], r => [r.user?.label, (r.technique || r.software)?.label]];
  return [map[0], ...rows.map(map[1])].map(l => l.map(cell).join(",")).join("\r\n");
}

// Paged records for one request, with "show more" and CSV download.
export function RecordList({ request, ui, onNavigate, onMeta, csvName, footer = true, stix = true }) {
  const { B, fs, HP } = ui;
  const [rows, setRows] = useState([]);
  const [meta, setMeta] = useState(null);
  // Rows belong to the request that loaded them; never draw one dataset's rows with another's layout.
  const [loadedFor, setLoadedFor] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    let live = true;
    setRows([]); setMeta(null); setLoading(true); setError("");
    getJson(rowsUrl(request.params, 0), { fresh: true })
      .then(d => { if (live) { setRows(d.rows); setMeta(d); setLoadedFor(request); onMeta?.(d); } })
      .catch(e => live && setError(e.message))
      .finally(() => live && setLoading(false));
    return () => { live = false; };
  }, [request]); // eslint-disable-line react-hooks/exhaustive-deps

  const more = () => {
    setLoading(true);
    getJson(rowsUrl(request.params, rows.length), { fresh: true })
      .then(d => setRows(r => [...r, ...d.rows]))
      .catch(e => setError(e.message))
      .finally(() => setLoading(false));
  };

  const download = async () => {
    // Up to 2,000 rows, fetched in pages.
    let all = [...rows];
    while (meta && all.length < Math.min(meta.total, 2000)) {
      const d = await getJson(rowsUrl(request.params, all.length), { fresh: true });
      if (!d.rows.length) break;
      all = all.concat(d.rows);
    }
    const url = URL.createObjectURL(new Blob([toCsv(request.params.dataset, all)], { type: "text/csv" }));
    const a = Object.assign(document.createElement("a"), { href: url, download: `${(csvName || request.title || "records").toLowerCase().replace(/[^a-z0-9]+/g, "-")}.csv` });
    document.body.appendChild(a); a.click(); a.remove();
    setTimeout(() => URL.revokeObjectURL(url), 1000);
  };

  return (
    <>
      {error && <div style={{ color: "#dc2626", fontSize: fs(12), padding: "12px 0" }}>{error}</div>}
      {loadedFor === request && rows.map((r, i) => (
        <div key={i} style={{ padding: "10px 0", borderTop: i ? `1px solid ${B.border}` : "none" }}>
          <Row dataset={request.params.dataset} r={r} ui={ui} onNavigate={onNavigate} />
        </div>
      ))}
      {!loading && meta && rows.length === 0 && <div style={{ fontSize: fs(13), color: B.textDim, padding: "16px 0" }}>No records.</div>}
      {loading && <div style={{ fontSize: fs(12), color: B.textDim, padding: "12px 0" }}>Loading…</div>}
      {meta && rows.length < meta.total && !loading && (
        <button onClick={more} style={{ ...HP.navBtn, width: "100%", marginTop: 10, padding: "9px" }}>Show more ({nf(meta.total - rows.length)} left)</button>
      )}
      {footer && meta && meta.total > 0 && (
        <div style={{ marginTop: 14, display: "flex", justifyContent: "flex-end", gap: 8, flexWrap: "wrap" }}>
          {stix && <StixExportLink ui={ui} cap={2000} href={stixUrl(request.params, request.title)} label={`Export STIX${meta.total > 2000 ? " (first 2,000)" : ""}`} />}
          <button onClick={download} style={{ ...HP.navBtn, padding: "6px 12px" }}>Download CSV{meta.total > 2000 ? " (first 2,000)" : ""}</button>
        </div>
      )}
    </>
  );
}

export function DrillDrawer({ request, ui, onClose }) {
  const { B, fs } = ui;
  const [meta, setMeta] = useState(null);
  const panelRef = useRef(null);

  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    document.addEventListener("keydown", onKey);
    panelRef.current?.focus();
    return () => document.removeEventListener("keydown", onKey);
  }, [onClose]);

  return (
    <div style={{ position: "fixed", inset: 0, zIndex: 70, display: "flex", justifyContent: "flex-end" }}>
      <div onClick={onClose} style={{ position: "absolute", inset: 0, background: "rgba(0,0,0,0.45)" }} />
      <aside ref={panelRef} tabIndex={-1} role="dialog" aria-modal="true" aria-label={request.title}
        style={{ position: "relative", width: "min(560px, 100vw)", height: "100%", background: B.bgPanel, borderLeft: `1px solid ${B.borderLight}`, boxShadow: "-16px 0 40px rgba(0,0,0,0.35)", display: "flex", flexDirection: "column", outline: "none", fontFamily: "'Satoshi', sans-serif" }}>
        <div style={{ padding: "18px 20px 12px", borderBottom: `1px solid ${B.border}` }}>
          <div style={{ display: "flex", gap: 10, alignItems: "flex-start" }}>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: fs(17), fontWeight: 900, color: B.text }}>{request.title}</div>
              {request.subtitle && <div style={{ fontSize: fs(12), color: B.textMuted, marginTop: 3 }}>{request.subtitle}</div>}
              {meta && <div style={{ fontSize: fs(11), color: B.textDim, marginTop: 6 }}>{nf(meta.total)} record{meta.total === 1 ? "" : "s"} · {meta.datasetLabel} ({meta.source}){meta.scope?.inferred ? " · actor links inferred through their software" : ""}</div>}
            </div>
            <button onClick={onClose} aria-label="Close" style={{ background: "none", border: "none", color: B.textMuted, fontSize: fs(20), cursor: "pointer", lineHeight: 1 }}>×</button>
          </div>
          {request.entity && (
            <a href={kbHref(request.entity.link)} onClick={onClose} style={{ display: "inline-block", marginTop: 10, fontSize: fs(12), color: B.accent, textDecoration: "none" }}>Open {request.entity.label}'s profile →</a>
          )}
          {request.params.dataset === "victims" && <div style={{ fontSize: fs(11), color: B.textDim, marginTop: 8 }}>Leak-site claims: the groups' own posts, not verified incidents.</div>}
        </div>

        <div style={{ flex: 1, overflowY: "auto", padding: "4px 20px 20px" }}>
          <RecordList request={request} ui={ui} onNavigate={onClose} onMeta={setMeta} />
        </div>
        <div style={{ padding: "8px 20px", borderTop: `1px solid ${B.border}`, fontSize: fs(11), color: B.textDim }}>Esc to close</div>
      </aside>
    </div>
  );
}
